import * as React from 'react'
import { Link, useLoaderData } from 'react-router-dom'

import { withQueryClientContext } from '../../../../app/shared/components/QueryClientContext'
import { Pagination } from '../../../components/pagination'
import { getStepPagination } from '../../../hooks/use-table-pagination'
import type { ApiPaginatedResponse } from '../../../types'
import { CenteredAlert } from '../components/CenteredAlert'
import Layout from '../components/Layout'
import { ListCard, ManagerHoldingsSection } from '../components/ListCard'
import { ManagersAndFundsSearch } from '../components/ManagersAndFundsSearch'
import { ManagersFundsSortSelect } from '../components/ManagersFundsSortSelect'
import { InsiderView, ManagerFundItemData } from '../constants'
import { useInsiderTradingRouter } from '../hooks/useInsiderTradingRouter'
import { useManagersListData } from '../hooks/useManagersListData'

function Managers() {
  const initialData = useLoaderData() as ApiPaginatedResponse<ManagerFundItemData[]> | null
  const insiderTradingRouter = useInsiderTradingRouter<InsiderView.Managers>()
  const { page: currentPage, search, sort } = insiderTradingRouter.viewParams
  const managersListData = useManagersListData({
    page: currentPage,
    initialData,
    search,
    sort,
    shouldResetScroll: true,
  })

  const { totalPages = 0 } = managersListData.data ?? {}
  const stepNavigation = React.useMemo(
    () => getStepPagination({ pages: totalPages, currentPage, maxSidePreviewPages: 3 }),
    [totalPages, currentPage]
  )
  const items = managersListData.data?.items ?? []

  return (
    <Layout
      middleContent={<ManagersAndFundsSearch searchIn="managers" />}
      rightContent={<ManagersFundsSortSelect view={InsiderView.Managers} sort={sort} />}
    >
      {items.length === 0 ? (
        <CenteredAlert>No managers match your search. Try using different keywords.</CenteredAlert>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-6 xl:grid-cols-3">
            {items.map((managerItem) => (
              <ListCard
                key={managerItem.filer.investorId}
                filer={managerItem.filer}
                latestSummaries={managerItem.latestSummaries}
                highlightHeaderLabel={search ?? undefined}
                href={insiderTradingRouter.getInsiderTradingLink({
                  view: InsiderView.ManagerDetails,
                  params: { investorId: managerItem.filer.investorId },
                })}
                bottomSection={<ManagerHoldingsSection topInvestments={managerItem.topInvestments} />}
              />
            ))}
          </div>

          <div className="flex w-full items-center justify-center overflow-hidden">
            <Pagination
              canGoBack={currentPage > 1}
              canGoForward={currentPage < totalPages}
              canGoToPage={(page) => page <= totalPages}
              visiblePages={stepNavigation}
              currentPage={currentPage}
              isLoading={managersListData.isFetching}
              getPageItemButtonProps={(page) => ({
                as: Link,
                to: insiderTradingRouter.getInsiderTradingLink({
                  view: InsiderView.Managers,
                  params: { ...insiderTradingRouter.viewParams, page },
                }),
              })}
            />
          </div>
        </>
      )}
    </Layout>
  )
}

export default withQueryClientContext(Managers)
